// ============================================================================
// Demand lines — the order's fulfilment lines, one BLOCK each rather than one
// table row each.
//
// This was an antd Table with ten columns (thumb / SO line / product / ordered /
// cancelled / status / availability / promised / actions / WH). Nine of them are
// short and fixed-width; the tenth is a product name that routinely runs past
// sixty characters. In a table they share one width, so the name — the thing an
// operator identifies the line BY — got the narrowest ration and wrapped into
// four lines while "Cancelled: 0 nos" sat on a comfortable column of its own.
//
// So the name gets a row to itself, full width, and the short facts get a fixed
// grid underneath it. The grid template is FIXED (not auto-fit) so the numbers
// still line up down the list: scanning "which line is short-shipped" reads the
// same as it did in the table.
// ============================================================================
import { Button, Empty, Input, InputNumber, Modal, Space, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import { fmtQtyUnit } from "../format";
import { getSalesOrder, giveGoAhead } from "../oms.api";
import { Allocation, EstimateLine, SalesOrderDetail as SODetail, SalesOrderLine } from "../types";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { LineActions } from "./escalation_actions";
import ProductThumb from "./product_thumb";
import StatusTag from "./status_tag";

// The go-ahead context for one awaiting line: the stage it POSTs to, plus EVERY awaiting line on the
// same allotment (the dimensions form collects a final quantity for each). The go-ahead is per
// ALLOCATION, so these are shared across the allotment's lines — releasing any one releases them all.
export interface GoAheadGroup {
  stageId: number;
  lines: SalesOrderLine[];
}

// line.id → its allotment's go-ahead group. Read purely from the backend's allotment fields: a line
// is "awaiting go-ahead" iff it appears in some allotment's `awaiting_go_ahead_line_ids` and that
// allotment carries a `go_ahead_stage_id`. Every awaiting line of one allotment maps to the SAME
// group, so each row's button opens the same per-line dimensions form and posts to the same stage.
export function goAheadByLine(
  allocations: Allocation[],
  lines: SalesOrderLine[]
): Map<number, GoAheadGroup> {
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const m = new Map<number, GoAheadGroup>();
  for (const a of allocations ?? []) {
    if (a.go_ahead_stage_id == null) continue;
    const ids = a.awaiting_go_ahead_line_ids ?? [];
    if (ids.length === 0) continue;
    const group: GoAheadGroup = {
      stageId: a.go_ahead_stage_id,
      lines: ids
        .map((id) => lineById.get(id))
        .filter((l): l is SalesOrderLine => l != null),
    };
    for (const id of ids) m.set(id, group);
  }
  return m;
}

// The "Give go-ahead" control for a held customizable line. Opens a dimensions form — one final
// measured quantity (e.g. sq ft) per awaiting line on the allotment — then POSTs it as
// `line_quantities` to the allotment's go-ahead stage. That revises the SUPPLIER PO only; the
// refreshed order it returns carries the new delivery dates and cleared badges, which we hand to
// onDone so the page re-renders from it (no refetch, no guessing dates). "Approve as ordered" sends
// no quantities for an unchanged render. Gated on oms.update (admin) or oms.po (procurement PO desk).
const GoAheadButton: React.FC<{ group: GoAheadGroup; onDone: (so: SODetail) => void }> = ({
  group,
  onDone,
}) => {
  const { hasPermission } = usePermissions();
  const [open, setOpen] = useState(false);
  // Final measured qty per line id, as the raw InputNumber value. Blank / null / 0 means the line
  // was NOT re-measured — it's left out of the payload and its PO is untouched.
  const [qtys, setQtys] = useState<Record<number, number | null>>({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!hasPermission("oms.update") && !hasPermission("oms.po")) return null;

  const openDialog = () => {
    // Start blank, deliberately: a quantity is sent ONLY for a line that was actually re-measured.
    // A line left blank keeps its PO unchanged, and all-blank approves-and-releases as ordered.
    setQtys({});
    setNotes("");
    setOpen(true);
  };

  const submit = async () => {
    // Keys are the SAME sales_order_line_ids the backend handed us in awaiting_go_ahead_line_ids.
    // Only re-measured lines (a positive value) go in; blank / null / 0 is left out so its PO is
    // untouched. An empty object approves-and-releases with no PO change.
    const lineQuantities: Record<number, number> = {};
    for (const l of group.lines) {
      const v = qtys[l.id];
      if (v != null && v > 0) lineQuantities[l.id] = v;
    }
    setSubmitting(true);
    const so = await giveGoAhead(group.stageId, lineQuantities, notes.trim() || undefined);
    setSubmitting(false);
    if (so) {
      onDone(so);
      setOpen(false);
    }
  };

  return (
    <>
      <Tooltip title="This item is customizable and held pending a go-ahead. Approve to start production.">
        <Button type="primary" size="small" onClick={openDialog}>
          Give go-ahead
        </Button>
      </Tooltip>
      <Modal
        title="Give go-ahead for production"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText="Give go-ahead"
        okButtonProps={{ loading: submitting }}
        destroyOnClose
        centered
      >
        <p style={{ color: "#888", marginTop: 0 }}>
          This is a customizable order held until approval. Giving the go-ahead starts the production
          lead time (about 4 working days) and moves the order's delivery dates forward. Enter the
          final measured quantity only for lines that were re-measured — this revises the supplier PO
          only; the customer's price and quantity do not change. Leave a line blank to keep its PO
          unchanged; leave them all blank to release the order as ordered. The whole allotment's held
          lines are released together.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
          {group.lines.map((l) => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
                  {l.product?.product_name || `Line ${l.id}`}
                </div>
                <div style={{ fontSize: 11, color: "#999" }}>
                  Line {l.id} · ordered {fmtQtyUnit(l.ordered_qty, l.unit)}
                </div>
              </div>
              <InputNumber
                min={0}
                step={0.01}
                value={qtys[l.id] ?? null}
                onChange={(v) => setQtys((q) => ({ ...q, [l.id]: v as number | null }))}
                placeholder="final qty"
                style={{ width: 140 }}
                addonAfter={l.unit || undefined}
              />
            </div>
          ))}
        </div>
        <Input.TextArea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional — a note kept on the order timeline (e.g. render approved)"
        />
      </Modal>
    </>
  );
};

// The short facts, in a fixed order. Widths are minimums with a growth ratio, so the
// block reflows on a narrow screen without the columns drifting out of alignment.
const FACT_GRID =
  "minmax(96px, 0.8fr) minmax(96px, 0.8fr) minmax(110px, 0.9fr) minmax(180px, 1.4fr) minmax(120px, 1fr) minmax(110px, 1fr)";

const dash = <span style={{ color: "#bbb" }}>—</span>;

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 11,
        color: "#8c8c8c",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: 2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 13 }}>{children}</div>
  </div>
);

const AvailabilityFact: React.FC<{
  line: SalesOrderLine;
  isAdmin: boolean;
  onConfirm: () => void;
}> = ({ line, isAdmin, onConfirm }) =>
  line.availability_confirmed ? (
    <>
      <div>{line.lead_time != null ? `${line.lead_time}d lead` : "confirmed"}</div>
      {line.source_wh_name && (
        <div style={{ fontSize: 11, color: "#999" }}>from {line.source_wh_name}</div>
      )}
    </>
  ) : (
    <Space size={4} wrap>
      <Tag color="orange" style={{ marginInlineEnd: 0 }}>
        awaiting
      </Tag>
      {isAdmin && (
        <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={onConfirm}>
          Set lead time
        </Button>
      )}
    </Space>
  );

const DemandLineBlock: React.FC<{
  line: SalesOrderLine;
  first: boolean;
  isAdmin: boolean;
  onDone: (so: SODetail) => void;
  onConfirm: () => void;
  estimateLines?: EstimateLine[];
  outstandingCreditRupees?: string;
  soId?: number;
  // The go-ahead group this line belongs to, when the backend is holding it awaiting a go-ahead.
  // Undefined for an ordinary line (no button).
  goAhead?: GoAheadGroup;
  // Opens the parent's Batch return modal scoped to just this line. The dialog is owned by the
  // SO page so Confirm / Refund / Return all share one visual language and one post-submit refresh.
  onReturnClick?: () => void;
  onRefundClick?: () => void;
}> = ({
  line,
  first,
  isAdmin,
  onDone,
  onConfirm,
  estimateLines,
  outstandingCreditRupees,
  soId,
  goAhead,
  onReturnClick,
  onRefundClick,
}) => {
  const name = line.product?.product_name;
  return (
    <div
      style={{
        padding: "12px 16px",
        borderTop: first ? undefined : "1px solid #f0f0f0",
      }}
    >
      {/* Row 1 — identity. The name owns the full width and is free to wrap. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <ProductThumb src={line.product?.image_url} alt={name || undefined} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, lineHeight: 1.35 }}>{name || "—"}</div>
          {(line.product?.private_label_name || line.product?.sku) && (
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
              {line.product?.private_label_name && (
                <span>Private label: {line.product.private_label_name}</span>
              )}
              {line.product?.private_label_name && line.product?.sku ? " · " : ""}
              {line.product?.sku && <span>SKU {line.product.sku}</span>}
            </div>
          )}
        </div>
        {/* Identity and state ride at the top right, where they stay put as the name wraps.
            "SO Line" is named the way every other surface names it (the allocation ledger's
            SO Line column, an escalation's "Line #1385") so a row can be matched across them. */}
        <Space size={8} align="start" style={{ flexShrink: 0 }}>
          <Tooltip title="SO line id">
            <span style={{ fontSize: 12, color: "#8c8c8c", whiteSpace: "nowrap" }}>
              Line {line.id}
            </span>
          </Tooltip>
          <StatusTag status={line.status} />
          {goAhead && <GoAheadButton group={goAhead} onDone={onDone} />}
          <LineActions
            line={line}
            onDone={onDone}
            estimateLines={estimateLines}
            outstandingCreditRupees={outstandingCreditRupees}
            onReturnClick={onReturnClick}
            onRefundClick={onRefundClick}
          />
        </Space>
      </div>

      {/* Row 2 — the short facts, on their own aligned grid. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: FACT_GRID,
          gap: "8px 16px",
          marginTop: 10,
          paddingLeft: 52, // clears the thumbnail, so the facts sit under the name
        }}
      >
        <Fact label="Ordered">{fmtQtyUnit(line.ordered_qty, line.unit)}</Fact>
        <Fact label="Cancelled">
          {Number(line.cancelled_qty) > 0 ? (
            <span style={{ color: "#cf1322" }}>{fmtQtyUnit(line.cancelled_qty, line.unit)}</span>
          ) : (
            fmtQtyUnit(line.cancelled_qty, line.unit)
          )}
        </Fact>
        {/* Why the refund actions appeared on this line and not the one above it. Without this the
            kebab menu looks arbitrary — an operator cannot see what the refund can reach. */}
        <Fact label="Refundable">
          {Number(line.refundable_qty ?? 0) > 0 ? (
            <Tooltip title="Quantity no allotment holds — handed back by a deallocation, or never sourced. Nothing here has shipped, so it can be refunded rather than returned.">
              <span style={{ color: "#d46b08" }}>
                {fmtQtyUnit(line.refundable_qty, line.unit)}
              </span>
            </Tooltip>
          ) : (
            dash
          )}
        </Fact>
        <Fact label="Availability">
          <AvailabilityFact line={line} isAdmin={isAdmin} onConfirm={onConfirm} />
        </Fact>
        <Fact label="Promised">{line.promised_delivery_date || dash}</Fact>
        <Fact label="WH">
          {line.wh_name || (line.wh_id != null ? `WH #${line.wh_id}` : dash)}
        </Fact>
      </div>
    </div>
  );
};

const DemandLines: React.FC<{
  lines: SalesOrderLine[];
  isAdmin: boolean;
  onDone: (so: SODetail) => void;
  // Opens the order's WHOLE pending set, not just this line — the planner can only merge demand
  // into a shared allocation/PO within one request, so a per-line confirm raises a PO per line
  // even when they share a supplier and a lead time.
  onConfirm: () => void;
  estimateLines?: EstimateLine[];
  // Threaded from the order's payment block — every line's wallet action needs the same figure.
  outstandingCreditRupees?: string;
  // SO id — needed so the damage modal can refetch the parent SO after filing (the shared pool
  // shrinks so damageable_qty + returnable_qty on sibling lines also change).
  soId?: number;
  // The order's allotments — the only source of the customization go-ahead badges. A line shows the
  // "Give go-ahead" button iff it appears in one of these allotments' `awaiting_go_ahead_line_ids`.
  allocations?: Allocation[];
  // Fired when a line's Actions → Return is picked. Parent opens its Batch return modal scoped
  // to that one line. Omitted = the row-level Return action does nothing (undefined custom click),
  // same escape-hatch pattern Damage uses.
  onReturnLine?: (line: SalesOrderLine) => void;
  onRefundLine?: (line: SalesOrderLine) => void;
}> = ({ lines, isAdmin, onDone, onConfirm, estimateLines, outstandingCreditRupees, soId, allocations, onReturnLine, onRefundLine }) => {
  const goAheadGroups = React.useMemo(
    () => goAheadByLine(allocations ?? [], lines),
    [allocations, lines]
  );
  if (!lines.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No demand lines" />;
  }
  return (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}>
      {lines.map((line, i) => (
        <DemandLineBlock
          key={line.id}
          line={line}
          first={i === 0}
          isAdmin={isAdmin}
          onDone={onDone}
          onConfirm={onConfirm}
          estimateLines={estimateLines}
          outstandingCreditRupees={outstandingCreditRupees}
          soId={soId}
          goAhead={goAheadGroups.get(line.id)}
          onReturnClick={onReturnLine ? () => onReturnLine(line) : undefined}
          onRefundClick={onRefundLine ? () => onRefundLine(line) : undefined}
        />
      ))}
    </div>
  );
};

export default DemandLines;
